import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface Bowler {
  id: number;
  name: string;
  email: string;
  playerId: number;
  joinedAt: string;
  leaguesCount: number;
  leagueNames: string;
  gender: string | null;
  dob: string | null;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalBowlers: number;
  limit: number;
}

interface BowlersData {
  bowlers: Bowler[];
  pagination: PaginationInfo;
}

const BowlersManagement: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [bowlersData, setBowlersData] = useState<BowlersData | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState<string>('');

  const fetchBowlers = async () => {
    try {
      setLoading(true);
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const searchParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20'
      });

      if (searchTerm.trim()) {
        searchParams.append('search', searchTerm.trim());
      }

      const response = await fetch(`${apiUrl}/bowlers?${searchParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBowlersData(data);
        setError('');
      } else {
        setError('Failed to fetch bowlers data');
      }
    } catch (error) {
      console.error('Failed to fetch bowlers:', error);
      setError('Failed to fetch bowlers data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBowlers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleBowlerClick = (bowlerId: number) => {
    navigate(`/admin/bowlers/${bowlerId}`);
  };

  const renderPagination = () => {
    if (!bowlersData || bowlersData.pagination.totalPages <= 1) return null;

    const { currentPage, totalPages } = bowlersData.pagination;
    const pages = [];

    // Show up to 5 page numbers
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + 4);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-center space-x-2 mt-6">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-2 rounded-lg bg-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20 transition duration-200"
        >
          {t('admin.bowlersManagement.previous')}
        </button>
        
        {pages.map(page => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`px-3 py-2 rounded-lg transition duration-200 ${
              page === currentPage
                ? 'bg-purple-600 text-white font-bold'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            {page}
          </button>
        ))}
        
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-2 rounded-lg bg-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20 transition duration-200"
        >
          {t('admin.bowlersManagement.next')}
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-white hover:text-purple-300 transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-3xl font-bold text-white">{t('admin.bowlersManagement.title')}</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Stats */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 space-y-4 sm:space-y-0">
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder={t('admin.bowlersManagement.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <p className="text-purple-300 text-xs mt-1">
              {t('admin.bowlersManagement.searchHint')}
            </p>
          </div>
          
          {bowlersData && (
            <div className="flex items-center space-x-4">
              <div className="bg-white/10 backdrop-blur-lg rounded-lg px-4 py-2 border border-white/20">
                <span className="text-purple-200 text-sm">{t('admin.bowlersManagement.totalBowlers')}: </span>
                <span className="text-white font-bold">{bowlersData.pagination.totalBowlers}</span>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-lg px-4 py-2 border border-white/20">
                <span className="text-purple-200 text-sm">{t('admin.bowlersManagement.page')}: </span>
                <span className="text-white font-bold">
                  {bowlersData.pagination.currentPage} of {bowlersData.pagination.totalPages}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-500/20 border border-red-400 rounded-lg p-4 mb-6">
            <p className="text-red-200">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-white">{t('admin.bowlersManagement.loadingBowlers')}</p>
          </div>
        )}

        {/* Bowlers List */}
        {!loading && bowlersData && (
          <>
            {bowlersData.bowlers.length === 0 ? (
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20 text-center">
                <div className="text-4xl mb-2">👥</div>
                {searchTerm ? (
                  <>
                    <p className="text-purple-200">{t('admin.bowlersManagement.noBowlersFound', { searchTerm })}</p>
                    <p className="text-purple-300 text-sm">{t('admin.bowlersManagement.tryAdjusting')}</p>
                  </>
                ) : (
                  <>
                    <p className="text-purple-200">{t('admin.bowlersManagement.noBowlersYet')}</p>
                    <p className="text-purple-300 text-sm">{t('admin.bowlersManagement.bowlersWillAppear')}</p>
                  </>
                )}
              </div>
            ) : (
              <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
                <div className="max-h-[40em] overflow-y-auto">
                  <div className="divide-y divide-white/10">
                    {bowlersData.bowlers.map((bowler) => (
                      <div
                        key={bowler.id}
                        onClick={() => handleBowlerClick(bowler.id)}
                        className="p-4 hover:bg-white/10 transition duration-200 cursor-pointer"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-lg">
                                {bowler.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-white">{bowler.name}</h3>
                              <p className="text-purple-200 text-sm">{bowler.email}</p>
                              <div className="flex items-center space-x-4 mt-1 text-xs text-purple-300">
                                <span>{t('admin.bowlersManagement.bowlerId')}: {bowler.id}</span>
                                {bowler.gender && (
                                  <span className="bg-purple-900/50 border border-purple-500/30 px-1.5 py-0.5 rounded text-purple-300">
                                    {bowler.gender === 'M' ? 'Male' : 'Female'}
                                  </span>
                                )}
                                {bowler.dob && (
                                  <span className="text-purple-400">
                                    Age {Math.floor((Date.now() - new Date(bowler.dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000))}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-4">
                            <div className="text-center">
                              <p className="text-lg font-bold text-white">{bowler.leaguesCount}</p>
                              <p className="text-purple-200 text-xs">{t('admin.bowlersManagement.leagues')}</p>
                            </div>
                            <div className="text-purple-300 max-w-xs truncate">
                              <p className="text-sm">{bowler.leagueNames}</p>
                            </div>
                            <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Pagination */}
                {renderPagination()}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BowlersManagement;